// src/shared/components/DevMobileEmulator/useDevMobileMode.ts
//
// Decides whether the mobile-phone emulator should be active.
//
// Auto-activates when ALL conditions are true:
//   1. Running on a dev hostname (localhost, 127.0.0.1, 10.x, 192.168.x, *.local)
//   2. Viewport is desktop-sized (≥ 900px both axes — i.e. it's a laptop/monitor)
//   3. URL has ?mobile=1  OR  the user toggled it on via Ctrl+Shift+M
//
// The toggle persists in sessionStorage so it survives hot-reloads.

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = '__pac_dev_mobile_emu';

function isDevHost(): boolean {
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h.startsWith('10.') ||
    h.startsWith('192.168.') ||
    h.endsWith('.local')
  );
}

function isDesktopViewport(): boolean {
  return window.innerWidth >= 900 && window.innerHeight >= 700;
}

function hasQueryParam(): boolean {
  return new URLSearchParams(window.location.search).get('mobile') === '1';
}

export function useDevMobileMode() {
  const [active, setActive] = useState<boolean>(() => {
    // initialise from sessionStorage, query param, or off
    if (typeof window === 'undefined') return false;
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    // auto-detect: dev host + desktop viewport + ?mobile=1
    return isDevHost() && isDesktopViewport() && hasQueryParam();
  });

  const toggle = useCallback(() => {
    setActive((prev) => {
      const next = !prev;
      sessionStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  // keyboard shortcut: Ctrl+Shift+M
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggle]);

  return { active, toggle };
}
